import React, { useEffect, useState } from 'react';
import useInView from './useInView';
import useMotionPrefs from './useMotionPrefs';

/* ==================================================================
   AnimatedProgress
   ------------------------------------------------------------------
   A bar that fills from 0 to `value` the first time it is seen, and
   then tracks the value normally (so a profile that ticks from 85 to
   92 animates the last 7 points, not the whole bar again).
   ================================================================== */

export default function AnimatedProgress({
  value = 0,
  height = 8,
  onDark = false,
  delay = 120,
  className = '',
  style,
}) {
  const { reduced } = useMotionPrefs();
  const [ref, inView] = useInView({ threshold: 0.3, skip: reduced });
  const [width, setWidth] = useState(reduced ? value : 0);

  useEffect(() => {
    if (reduced) { setWidth(value); return undefined; }
    if (!inView) return undefined;
    const id = setTimeout(() => setWidth(value), delay);
    return () => clearTimeout(id);
  }, [inView, value, delay, reduced]);

  return (
    <div
      ref={ref}
      className={`ui-progress${onDark ? ' ui-progress--on-dark' : ''} ${className}`.trim()}
      style={{ height, ...style }}
      role="progressbar"
      aria-valuenow={Math.round(value)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="ui-progress__fill" style={{ width: `${Math.max(0, Math.min(100, width))}%` }} />
    </div>
  );
}
